// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useStargates } from './StargatesContext';
import { useSharedMqtt } from './SharedMqttProvider';
import { useAuth } from './AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { isMaintenance } from '../utils/maintenanceMode';
import { isDeviceOnline } from '../utils/healthStatus';
import { fmtTime, fmtWithTZ } from '../utils/timezoneUtils';

const AlertsContext = createContext();

export function useAlerts() {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }
  return context;
}

// Severity ordering for sorting: critical first, then warning, then success, then info
const SEVERITY_ORDER = { critical: 0, warning: 1, success: 2, info: 3 };

// How long "info" alerts live before auto-dismissing (ms)
const INFO_AUTO_EXPIRE_MS = 30000;

// How long "success" (recovery / good-news) alerts live before auto-dismissing (ms).
// Good news must not sit in the banner inflating the "active alerts" count.
const SUCCESS_AUTO_EXPIRE_MS = 60000;

// How often to poll for data gap alarms (ms)
const DATA_GAP_POLL_INTERVAL = 10000;

// A state flip (device offline/online) must persist this long before it alerts.
// Statuses are polled every ~10s, so this is ~3 consecutive observations; a
// device hovering at the staleness threshold no longer strobes the banner.
const OFFLINE_HOLD_MS = 30000;

// The MQTT connection must be down this long continuously before alerting.
// Keepalive flaps from browser tab throttling reconnect within a few seconds
// and should produce no alert at all.
const MQTT_LOSS_DEBOUNCE_MS = 12000;

const PERMANENT_DISMISS_KEY = 'starfleet_permanently_dismissed_alerts';

// Dismissal ids are per-incident (they embed a timestamp), so the set grows
// over time — cap it to keep localStorage bounded.
const MAX_PERMANENT_DISMISSALS = 200;

function loadPermanentDismissals() {
  try {
    const raw = localStorage.getItem(PERMANENT_DISMISS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function savePermanentDismissals(set) {
  try {
    localStorage.setItem(
      PERMANENT_DISMISS_KEY,
      JSON.stringify([...set].slice(-MAX_PERMANENT_DISMISSALS))
    );
  } catch {
    /* quota exceeded — ignore */
  }
}

function sameValue(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return a === b;
}

function formatDownDuration(ms) {
  const diffSec = Math.floor(ms / 1000);
  if (diffSec < 60) return `${diffSec}s`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ${diffSec % 60}s`;
  return `${Math.floor(diffSec / 3600)}h ${Math.floor((diffSec % 3600) / 60)}m`;
}

// Relative age rendered into alert messages so staleness is visible at a
// glance ("28d ago" reads very differently from a bare absolute date).
function formatAge(ms) {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export function AlertsProvider({ children }) {
  const [alerts, setAlerts] = useState([]);
  const [bannerVisible, setBannerVisible] = useState(true);
  const dismissedIdsRef = useRef(new Set());
  const permanentlyDismissedRef = useRef(loadPermanentDismissals());
  const prevStargateStatusRef = useRef(new Map());
  const dataGapTimerRef = useRef(null);
  const autoExpireTimersRef = useRef(new Map());

  // Consume existing context providers
  const { currentUser } = useAuth();
  const { stargates } = useStargates();
  const sharedMqtt = useSharedMqtt();
  const mqttConnected = sharedMqtt?.isConnected || false;
  const mqttLossTimerRef = useRef(null);
  const mqttLossAlertIdRef = useRef(null); // id of the currently-shown loss alert
  const mqttEverConnectedRef = useRef(false);

  // ── Helper: remove an alert entirely ────────────────────────────────
  const removeAlert = useCallback((id) => {
    setAlerts((prev) => {
      const next = prev.filter((a) => a.id !== id);
      return next.length === prev.length ? prev : next;
    });
    const timer = autoExpireTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      autoExpireTimersRef.current.delete(id);
    }
  }, []);

  // ── Helper: add or update an alert ──────────────────────────────────
  const upsertAlert = useCallback(
    (alert) => {
      if (permanentlyDismissedRef.current.has(alert.id)) return;
      if (dismissedIdsRef.current.has(alert.id)) return;

      setAlerts((prev) => {
        const idx = prev.findIndex((a) => a.id === alert.id);
        if (idx < 0) {
          return [
            ...prev,
            {
              ...alert,
              dismissed: false,
              timestamp: alert.timestamp || new Date().toISOString(),
            },
          ];
        }
        const cur = prev[idx];
        const merged = { ...cur, ...alert, timestamp: alert.timestamp || cur.timestamp };
        // Bail out when nothing actually changed so 10s polls that re-upsert
        // identical alerts don't re-render every alerts consumer.
        if (Object.keys(merged).every((k) => sameValue(merged[k], cur[k]))) return prev;
        const updated = [...prev];
        updated[idx] = merged;
        return updated;
      });

      // Auto-expire: info and success alerts fade on their own; any alert can
      // opt in with a numeric autoExpire (ms) or opt out with autoExpire: false.
      const expires =
        alert.autoExpire !== false &&
        (typeof alert.autoExpire === 'number' ||
          alert.severity === 'info' ||
          alert.severity === 'success');
      if (expires) {
        const expireMs =
          typeof alert.autoExpire === 'number'
            ? alert.autoExpire
            : alert.severity === 'success'
              ? SUCCESS_AUTO_EXPIRE_MS
              : INFO_AUTO_EXPIRE_MS;
        const existing = autoExpireTimersRef.current.get(alert.id);
        if (existing) clearTimeout(existing);
        const timer = setTimeout(() => {
          autoExpireTimersRef.current.delete(alert.id);
          removeAlert(alert.id);
        }, expireMs);
        autoExpireTimersRef.current.set(alert.id, timer);
      } else {
        // A non-expiring upsert must cancel any expiry timer left by a
        // previous incarnation of this id (e.g. firmware "completed" success
        // → a new update starts within 60s and reuses the id), or the stale
        // timer fires and deletes the live alert.
        const stale = autoExpireTimersRef.current.get(alert.id);
        if (stale) {
          clearTimeout(stale);
          autoExpireTimersRef.current.delete(alert.id);
        }
      }
    },
    [removeAlert]
  );

  // ── Public: dismiss an alert (user action, this session only) ───────
  const dismissAlert = useCallback(
    (id) => {
      dismissedIdsRef.current.add(id);
      removeAlert(id);
    },
    [removeAlert]
  );

  // ── Public: dismiss all alerts ──────────────────────────────────────
  const dismissAll = useCallback(() => {
    setAlerts((prev) => {
      prev.forEach((a) => dismissedIdsRef.current.add(a.id));
      return [];
    });
    // Clear all auto-expire timers
    for (const timer of autoExpireTimersRef.current.values()) {
      clearTimeout(timer);
    }
    autoExpireTimersRef.current.clear();
  }, []);

  // ── Public: permanently dismiss an alert (persisted in localStorage) ─
  // Ids are per-incident, so this hides THIS occurrence across reloads
  // without swallowing future incidents of the same kind.
  const permanentlyDismissAlert = useCallback(
    (id) => {
      permanentlyDismissedRef.current.add(id);
      savePermanentDismissals(permanentlyDismissedRef.current);
      removeAlert(id);
    },
    [removeAlert]
  );

  // ── Public: acknowledge a data-gap alert server-side ────────────────
  // The trash can on a gap alert acknowledges the underlying gap rows via
  // the backend, so the dismissal is shared by every browser and every user
  // instead of hiding the alert in this browser's localStorage.
  const acknowledgeGapAlert = useCallback(
    async (alert) => {
      dismissedIdsRef.current.add(alert.id);
      removeAlert(alert.id);
      const results = await Promise.allSettled(
        (alert.gapIds || []).map((gapId) =>
          axiosInstance.put(`/data-gaps/${gapId}/acknowledge`)
        )
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        // Un-suppress so the next poll resurrects the alert — a failed ack
        // must not stay hidden for the rest of the session.
        dismissedIdsRef.current.delete(alert.id);
        console.warn(
          `[AlertsContext] ${failed}/${results.length} gap acknowledgements failed — the alert will reappear`
        );
      }
    },
    [removeAlert]
  );

  // ── Public: clear all permanent dismissals (for admin/reset use) ────
  const clearPermanentDismissals = useCallback(() => {
    permanentlyDismissedRef.current.clear();
    savePermanentDismissals(permanentlyDismissedRef.current);
  }, []);

  // ══════════════════════════════════════════════════════════════════════
  // 1. STARGATE STATUS TRANSITION DETECTION (with hysteresis)
  //
  // health_status is the canonical reachability signal (green/yellow =
  // reachable, red = offline). connection_status only mirrors the MQTT
  // session and lags the health writer — OR-ing the two caused false
  // offline/online alert pairs. A flip must hold for OFFLINE_HOLD_MS
  // before it alerts; brief blips produce no alert at all.
  // ══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!stargates || stargates.length === 0) return;
    const prevMap = prevStargateStatusRef.current;
    const now = Date.now();

    stargates.forEach((sg) => {
      const rawOffline = !isDeviceOnline(sg.health_status);
      const entry = prevMap.get(sg.id);

      // First sighting (initial load or newly added stargate): seed silently —
      // no alert burst for devices that were already offline before we watched.
      if (!entry) {
        prevMap.set(sg.id, {
          stableOffline: rawOffline,
          pendingSince: null,
          offlineSince: null,
          incidentId: null,
        });
        return;
      }

      if (rawOffline === entry.stableOffline) {
        entry.pendingSince = null;
        return;
      }
      if (entry.pendingSince === null) {
        entry.pendingSince = now;
        return;
      }
      if (now - entry.pendingSince < OFFLINE_HOLD_MS) return;

      // The flip held — commit it and alert.
      const name = sg.friendly_name || sg.serial_number || `ID ${sg.id}`;
      const flippedAt = new Date(entry.pendingSince);
      entry.stableOffline = rawOffline;
      entry.pendingSince = null;

      if (rawOffline) {
        const id = `offline_${sg.id}_${now}`;
        entry.incidentId = id;
        entry.offlineSince = flippedAt;
        upsertAlert({
          id,
          type: 'stargate_offline',
          severity: 'critical',
          title: 'Stargate Offline',
          message: `${name} went offline · ${fmtTime(flippedAt)}`,
          stargateId: sg.id,
          incident: true,
          autoExpire: false,
        });
      } else {
        const id = entry.incidentId || `offline_${sg.id}_${now}`;
        const durationStr = entry.offlineSince
          ? ` · down ${formatDownDuration(flippedAt - entry.offlineSince)}`
          : '';
        entry.incidentId = null;
        entry.offlineSince = null;
        upsertAlert({
          id,
          type: 'stargate_recovered',
          severity: 'success',
          title: 'Stargate Back Online',
          message: `${name} reconnected · ${fmtTime(flippedAt)}${durationStr}`,
          stargateId: sg.id,
          incident: true,
        });
      }
    });
  }, [stargates, upsertAlert]);

  // ══════════════════════════════════════════════════════════════════════
  // 2. MQTT CONNECTION MONITORING (debounced)
  //
  // Only alert after the connection has been down MQTT_LOSS_DEBOUNCE_MS
  // straight, and only announce a recovery when a loss alert was actually
  // shown. This also kills the false "MQTT Reconnected" banner that used to
  // appear on every page load (initial connect is not a reconnect).
  // ══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!currentUser) {
      // Logged out — MQTT is intentionally disconnected; never alert.
      if (mqttLossTimerRef.current) {
        clearTimeout(mqttLossTimerRef.current);
        mqttLossTimerRef.current = null;
      }
      return;
    }

    if (mqttConnected) {
      mqttEverConnectedRef.current = true;
      if (mqttLossTimerRef.current) {
        clearTimeout(mqttLossTimerRef.current);
        mqttLossTimerRef.current = null;
      }
      if (mqttLossAlertIdRef.current) {
        const id = mqttLossAlertIdRef.current;
        mqttLossAlertIdRef.current = null;
        upsertAlert({
          id,
          type: 'mqtt_disconnected',
          severity: 'success',
          title: 'MQTT Reconnected',
          message: `Dashboard MQTT connection restored at ${fmtTime(new Date())}`,
          incident: true,
        });
      }
      return;
    }

    if (mqttLossTimerRef.current || mqttLossAlertIdRef.current) return;
    mqttLossTimerRef.current = setTimeout(() => {
      mqttLossTimerRef.current = null;
      const id = `mqtt_conn_${Date.now()}`;
      mqttLossAlertIdRef.current = id;
      const everConnected = mqttEverConnectedRef.current;
      upsertAlert({
        id,
        type: 'mqtt_disconnected',
        severity: 'critical',
        title: everConnected ? 'MQTT Connection Lost' : 'MQTT Not Connected',
        message: everConnected
          ? `Dashboard lost connection to the MQTT broker at ${fmtTime(new Date())}`
          : 'Dashboard has not reached the MQTT broker since page load — live updates are unavailable',
        incident: true,
        autoExpire: false,
      });
    }, MQTT_LOSS_DEBOUNCE_MS);
  }, [mqttConnected, currentUser, upsertAlert]);

  // ══════════════════════════════════════════════════════════════════════
  // 3. DATA GAP ALARM POLLING
  //
  // Unacknowledged gaps stay visible even after they resolve (a resolved
  // gap still means data was lost and nobody has acknowledged it). Alerts
  // only leave the banner when the gaps are acknowledged server-side.
  // ══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Provider stays mounted on /login — do not poll while logged out.
    if (!currentUser) {
      if (dataGapTimerRef.current) {
        clearInterval(dataGapTimerRef.current);
        dataGapTimerRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;

    const fetchGapAlarms = async () => {
      // Pause while a restore is swapping the databases (backend is down).
      if (isMaintenance()) return;
      if (!localStorage.getItem('token')) return;
      try {
        const res = await axiosInstance.get('/data-gaps', {
          params: { acknowledged: false, limit: 1000 },
        });
        if (cancelled) return;

        const gaps = res.data || [];
        const now = Date.now();

        // Consolidate gaps per stargate: one alert per stargate, not per gap
        const byStargate = new Map();
        gaps.forEach((gap) => {
          const sev = gap.severity;
          // 'resolved' gaps remain until acknowledged; anything else
          // (e.g. malformed rows) is skipped.
          if (sev !== 'warning' && sev !== 'critical' && sev !== 'resolved') return;
          const sgId = gap.stargate_id;
          if (!byStargate.has(sgId)) {
            byStargate.set(sgId, {
              name: gap.device_name || gap.device_serial || `Stargate #${sgId}`,
              serial: gap.device_serial || null,
              anyCritical: false,
              ongoing: 0,
              totalMissed: 0,
              count: 0,
              gapIds: [],
              latestGapStart: null,
              latestGapEnd: null,
              latestMetricType: null,
            });
          }
          const entry = byStargate.get(sgId);
          entry.count += 1;
          entry.gapIds.push(gap.id);
          entry.totalMissed += gap.missed_points || 0;
          if (!gap.gap_end && sev !== 'resolved') entry.ongoing += 1;
          if (sev === 'critical') entry.anyCritical = true;
          if (gap.gap_start) {
            const ts = new Date(gap.gap_start);
            if (!entry.latestGapStart || ts > entry.latestGapStart) {
              entry.latestGapStart = ts;
              entry.latestGapEnd = gap.gap_end ? new Date(gap.gap_end) : null;
              entry.latestMetricType = gap.metric_type || null;
            }
          }
        });

        const activeGapIds = new Set();
        for (const [sgId, info] of byStargate.entries()) {
          // Incident-scoped id: the newest gap row pins the id, so dismissing
          // one batch can't swallow gaps that appear later.
          info.gapIds.sort((a, b) => a - b);
          const alertId = `gap_sg_${sgId}_${info.gapIds[info.gapIds.length - 1]}`;
          activeGapIds.add(alertId);

          const stateStr = info.ongoing > 0 ? `${info.ongoing} ongoing` : 'all resolved';
          let whenStr = '';
          if (info.latestGapStart) {
            const abs = fmtWithTZ(info.latestGapStart, {
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            });
            // "started" — this is when the newest gap opened, NOT "last time we
            // had data". Chart can be healthy again while this timestamp is days old.
            whenStr = ` · started ${formatAge(now - info.latestGapStart.getTime())} (${abs})`;
          }
          upsertAlert({
            id: alertId,
            type: 'data_gap',
            severity: info.anyCritical ? 'critical' : 'warning',
            title: 'Data Gap',
            message:
              `${info.name} — ${info.count} gap${info.count !== 1 ? 's' : ''} (${stateStr}), ` +
              `${info.totalMissed} missed pts${whenStr}`,
            stargateId: sgId,
            deviceSerial: info.serial,
            gapIds: info.gapIds,
            gapStart: info.latestGapStart ? info.latestGapStart.getTime() : null,
            gapEnd: info.latestGapEnd ? info.latestGapEnd.getTime() : null,
            gapMetricType: info.latestMetricType,
            autoExpire: false,
          });
        }

        // Remove gap alerts that are no longer reported (i.e. acknowledged)
        setAlerts((prev) => {
          const next = prev.filter((a) => a.type !== 'data_gap' || activeGapIds.has(a.id));
          return next.length === prev.length ? prev : next;
        });
      } catch (err) {
        if (err.response?.status !== 401 && err.response?.status !== 404) {
          console.warn('[AlertsContext] Failed to fetch data gap alarms:', err.message);
        }
      }
    };

    fetchGapAlarms();
    dataGapTimerRef.current = setInterval(fetchGapAlarms, DATA_GAP_POLL_INTERVAL);

    // Background tabs throttle setInterval — refetch as soon as the tab is
    // visible again so alerts never look stale after returning to the page.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchGapAlarms();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (dataGapTimerRef.current) {
        clearInterval(dataGapTimerRef.current);
        dataGapTimerRef.current = null;
      }
    };
  }, [upsertAlert, currentUser]);

  // ══════════════════════════════════════════════════════════════════════
  // 4. GITLAB TOKEN EXPIRY — warn before the vault's pull/publish tokens lapse
  //    Source: /token-status.json (a deploy-time snapshot of each token's
  //    expiry DATE; days-remaining is computed live here so it stays accurate
  //    without re-querying GitLab from the browser).
  // ══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    let cancelled = false;
    const POLL = 6 * 60 * 60 * 1000; // 6h — expiry dates are static
    const fetchTokenExpiry = async () => {
      try {
        const res = await fetch('/token-status.json', { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const warnDays = data.warn_days || 30;
        const critDays = data.crit_days || 7;
        const now = Date.now();
        const seen = new Set();
        (data.tokens || []).forEach((t) => {
          const id = `gitlab_token_${t.key}`;
          const days = t.expires_at
            ? Math.floor((new Date(`${t.expires_at}T00:00:00Z`).getTime() - now) / 86400000)
            : null;
          const bad = t.revoked || t.active === false;
          // healthy + not near expiry → no alert
          if (!bad && (days === null || days > warnDays)) return;
          seen.add(id);
          const reason = t.revoked
            ? 'has been REVOKED'
            : t.active === false
              ? 'is INACTIVE'
              : days <= 0
                ? `has EXPIRED (${t.expires_at})`
                : `expires in ${days} day${days !== 1 ? 's' : ''} (${t.expires_at})`;
          upsertAlert({
            id,
            type: 'gitlab_token_expiry',
            severity: bad || (days !== null && days <= critDays) ? 'critical' : 'warning',
            title: 'GitLab token expiring',
            message: `${t.role || t.name} token ${reason}. Rotate it in GitLab and update the Ansible vault (ansible-vault edit).`,
            autoExpire: false,
          });
        });
        // clear token alerts that are now healthy
        setAlerts((prev) => {
          const next = prev.filter((a) => a.type !== 'gitlab_token_expiry' || seen.has(a.id));
          return next.length === prev.length ? prev : next;
        });
      } catch (e) {
        /* snapshot absent (old install) — ignore */
      }
    };
    fetchTokenExpiry();
    const timer = setInterval(fetchTokenExpiry, POLL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [upsertAlert]);

  // ── Cleanup timers on unmount ───────────────────────────────────────
  useEffect(() => {
    const timers = autoExpireTimersRef.current;
    const lossTimer = mqttLossTimerRef;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      if (lossTimer.current) {
        clearTimeout(lossTimer.current);
      }
    };
  }, []);

  // ── Derived values ──────────────────────────────────────────────────
  const activeAlerts = useMemo(
    () =>
      alerts
        .filter((a) => !a.dismissed)
        .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)),
    [alerts]
  );

  const toggleBanner = useCallback(() => setBannerVisible((v) => !v), []);
  const hideBanner = useCallback(() => setBannerVisible(false), []);
  const showBanner = useCallback(() => setBannerVisible(true), []);

  const value = useMemo(
    () => ({
      alerts: activeAlerts,
      alertCount: activeAlerts.length,
      bannerVisible,
      toggleBanner,
      hideBanner,
      showBanner,
      dismissAlert,
      dismissAll,
      permanentlyDismissAlert,
      acknowledgeGapAlert,
      clearPermanentDismissals,
      upsertAlert,
      removeAlert,
    }),
    [
      activeAlerts,
      bannerVisible,
      toggleBanner,
      hideBanner,
      showBanner,
      dismissAlert,
      dismissAll,
      permanentlyDismissAlert,
      acknowledgeGapAlert,
      clearPermanentDismissals,
      upsertAlert,
      removeAlert,
    ]
  );

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
}
